// ✅ src/pages/VehicleV2/TyreDetails.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/tyreDetails", tyreDetails);
//
// Endpoints used:
//   GET  https://nissi-india.com/api/tyreDetails/new/list?branchId=..&branchType=..&vehicleCode=..&vehicleName=..&vehicleRegNo=..&tyreSerialNo=..&tyreSize=..&tyreMake=..
//   GET  https://nissi-india.com/api/tyreDetails/retread/list?branchId=..&branchType=..&type=E|N&serialNo=..&tyreSize=..&position=..
//   POST https://nissi-india.com/api/tyreDetails/new/delete        body: { ids:[...] }
//   POST https://nissi-india.com/api/tyreDetails/retread/delete    body: { ids:[...] }
//
// ✅ Updated like BatteryCardEntry pattern:
// - Sends fin-year headers if available
// - Keeps your client-side pagination
// - Select All works for current page rows
// - Edit button for both tables
// - New Tyre Edit    -> /vehicle/AddNewTyre?tyreId=ID
// - Retread Edit     -> /vehicle/AddRetrededTyre?retreadTyreId=ID&type=E|N

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaFilter, FaTrash, FaEdit } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/tyreDetails";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

// ✅ mapper: backend rows -> frontend table keys
const mapNewTyreRow = (r, idx) => ({
  id: r.fld_NewTyre_ID ?? r.fld_newtyreid ?? r.NewTyreID ?? r.TyreID ?? r.id ?? idx + 1,
  branchName: r.fld_branchname ?? r.branchname ?? r.BranchName ?? "",
  purchaseDate: r.purchaseDate ?? r.fld_purchasedate ?? r.purchasedate ?? r.PurchaseDate ?? "",
  tyreSize: r.fld_Tyresize ?? r.fld_tyresize ?? r.tyresize ?? r.TyreSize ?? "",
  vehicleCode: r.vehicleCode ?? r.fld_vehicle_code ?? r.vehicle_code ?? r.VehicleCode ?? "",
  vehicleRegNo: r.vehicleRegNo ?? r.fld_vehicle_regno ?? r.vehicle_regno ?? r.VehicleRegNo ?? "",
  tyreSerialNo: r.fld_Tyreserialno ?? r.fld_serialno ?? r.serialno ?? r.SerialNo ?? "",
  tyreMake: r.fld_TyreMake ?? r.fld_make ?? r.make ?? r.TyreMake ?? "",
  tyreModel: r.fld_TyreModel ?? r.fld_model ?? r.model ?? r.TyreModel ?? "",
  tyrePosition: r.fld_TyrePosition ?? "",
  purchaseFrom: r.fld_Purchasefrom ?? r.fld_purchasefrom ?? r.purchasefrom ?? r.PurchaseFrom ?? "",
  tyreAmount: r.fld_TyreAmounts ?? r.fld_amount ?? r.amount ?? r.TyreAmount ?? "",
});

const mapRetreadRow = (r, idx) => ({
  id: r.fld_RetrededTyre_ID ?? r.fld_retrededtyre_id ?? r.RetrededTyreID ?? r.id ?? idx + 1,
  branchName: r.fld_branchname ?? r.branchname ?? r.BranchName ?? "",
  retreadDate: r.retreadDate ?? r.fld_Retrededdate ?? r.Retrededdate ?? r.RetreadDate ?? "",
  tyreSerialNo: r.fld_Tyreserialno ?? r.fld_serialno ?? r.serialno ?? r.SerialNo ?? "",
  tyreSize: r.fld_Tyresize ?? r.fld_tyresize ?? r.tyresize ?? r.TyreSize ?? "",
  tyrePosition: r.fld_TyrePosition ?? r.fld_position ?? r.position ?? r.TyrePosition ?? "",
  typeOfRetreaded: r.fld_Retrededtype ?? r.fld_typeofretreaded ?? r.typeofretreaded ?? r.TypeOfRetreaded ?? "",
  noOfRetreaded: r.fld_Noofretreded ?? r.fld_noofretreaded ?? r.noofretreaded ?? r.NoOfRetreaded ?? "",
  retreadedFrom: r.fld_Retrededfrom ?? r.fld_retreadedfrom ?? r.retreadedfrom ?? r.RetreadedFrom ?? "",
  amount: r.amount ?? r.fld_TyreAmount ?? r.Amount ?? "",
});

const TyreDetails = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const branchType = localStorage.getItem("branchtype") || "HO";
  const branchId = Number(localStorage.getItem("branchid") || 8);

  // ✅ financial year headers support (same pattern as BatteryCardEntry)
  const finYear =
    localStorage.getItem("finyear") ||
    localStorage.getItem("financialyear") ||
    sessionStorage.getItem("finyear") ||
    "";

  const finYearId =
    localStorage.getItem("finyearid") ||
    localStorage.getItem("financialyearid") ||
    sessionStorage.getItem("finyearid") ||
    "";

  const requestConfig = useMemo(() => {
    const headers = {};

    if (finYear) headers["x-finyear"] = finYear;
    if (finYearId) {
      headers["x-finyearid"] = finYearId;
      headers["x-finyear-id"] = finYearId;
    }

    return { headers };
  }, [finYear, finYearId]);

  // Filters (New)
  const [tyreSerialNo, setTyreSerialNo] = useState("");
  const [vehicleName, setVehicleName] = useState("");
  const [tyreSize, setTyreSize] = useState("");
  const [vehicleRegNo, setVehicleRegNo] = useState("");
  const [vehicleCode, setVehicleCode] = useState("");
  const [tyreMake, setTyreMake] = useState("");
  const [standViewAll, setStandViewAll] = useState("stand");

  // Retread filters
  const [retreadTyreType, setRetreadTyreType] = useState("existing"); // existing/new => E/N
  const [retreadTyreSerialNo, setRetreadTyreSerialNo] = useState("");
  const [retreadTyreSize, setRetreadTyreSize] = useState("");
  const [tyrePosition, setTyrePosition] = useState("Select");

  // Data
  const [loadingNew, setLoadingNew] = useState(false);
  const [loadingRetread, setLoadingRetread] = useState(false);
  const [newTyres, setNewTyres] = useState([]);
  const [retreadTyres, setRetreadTyres] = useState([]);

  // Selection
  const [selectedNewTyreIds, setSelectedNewTyreIds] = useState([]);
  const [selectedRetreadTyreIds, setSelectedRetreadTyreIds] = useState([]);

  const [selectAllNew, setSelectAllNew] = useState(false);
  const [selectAllRetread, setSelectAllRetread] = useState(false);

  const [showFilters, setShowFilters] = useState(true);

  // Pagination
  const [newPage, setNewPage] = useState(1);
  const [newPageSize, setNewPageSize] = useState(10);

  const [retPage, setRetPage] = useState(1);
  const [retPageSize, setRetPageSize] = useState(10);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark ? "bg-gray-700/30 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;

  const selectClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark ? "bg-gray-700/30 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const fetchNewList = useCallback(async () => {
    try {
      setLoadingNew(true);

      const params = {
        branchId,
        branchType,
        vehicleCode: standViewAll === "viewAll" ? "" : vehicleCode,
        vehicleName: standViewAll === "viewAll" ? "" : vehicleName,
        vehicleRegNo: standViewAll === "viewAll" ? "" : vehicleRegNo,
        tyreSerialNo: standViewAll === "viewAll" ? "" : tyreSerialNo,
        tyreSize: standViewAll === "viewAll" ? "" : tyreSize,
        tyreMake: standViewAll === "viewAll" ? "" : tyreMake,
      };

      const { data } = await axios.get(`${API_BASE}/new/list`, {
        params,
        ...requestConfig,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load new tyres");
      }

      const rows = (data.rows || []).map(mapNewTyreRow);
      setNewTyres(rows);
      setSelectedNewTyreIds([]);
      setSelectAllNew(false);
      setNewPage(1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Error loading new tyres");
      setNewTyres([]);
    } finally {
      setLoadingNew(false);
    }
  }, [
    branchId,
    branchType,
    standViewAll,
    vehicleCode,
    vehicleName,
    vehicleRegNo,
    tyreSerialNo,
    tyreSize,
    tyreMake,
    requestConfig,
  ]);

  const fetchRetreadList = useCallback(async () => {
    try {
      setLoadingRetread(true);

      const type = retreadTyreType === "new" ? "N" : "E";

      const params = {
        branchId,
        branchType,
        type,
        serialNo: retreadTyreSerialNo,
        tyreSize: retreadTyreSize,
        position: tyrePosition || "Select",
      };

      const { data } = await axios.get(`${API_BASE}/retread/list`, {
        params,
        ...requestConfig,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load retread tyres");
      }

      const rows = (data.rows || []).map(mapRetreadRow);
      setRetreadTyres(rows);
      setSelectedRetreadTyreIds([]);
      setSelectAllRetread(false);
      setRetPage(1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Error loading retread tyres");
      setRetreadTyres([]);
    } finally {
      setLoadingRetread(false);
    }
  }, [
    branchId,
    branchType,
    retreadTyreType,
    retreadTyreSerialNo,
    retreadTyreSize,
    tyrePosition,
    requestConfig,
  ]);

  useEffect(() => {
    fetchNewList();
    fetchRetreadList();
  }, [fetchNewList, fetchRetreadList]);

  // New pagination
  const newTotal = newTyres.length;
  const newTotalPages = Math.max(1, Math.ceil(newTotal / newPageSize));

  const pagedNewTyres = useMemo(() => {
    const start = (newPage - 1) * newPageSize;
    const end = start + newPageSize;
    return newTyres.slice(start, end);
  }, [newTyres, newPage, newPageSize]);

  // Retread pagination
  const retTotal = retreadTyres.length;
  const retTotalPages = Math.max(1, Math.ceil(retTotal / retPageSize));

  const pagedRetreadTyres = useMemo(() => {
    const start = (retPage - 1) * retPageSize;
    const end = start + retPageSize;
    return retreadTyres.slice(start, end);
  }, [retreadTyres, retPage, retPageSize]);

  const handleEditNewTyre = (id) => {
    const tyreId = toInt(id);
    if (!tyreId) return toast.warn("Invalid Tyre ID");
    navigate(`/vehicle/AddNewTyre?tyreId=${tyreId}`);
  };

  const handleEditRetreadTyre = (id) => {
    const retreadTyreId = toInt(id);
    if (!retreadTyreId) return toast.warn("Invalid Retread Tyre ID");

    const type = retreadTyreType === "new" ? "N" : "E";
    navigate(`/vehicle/AddRetrededTyre?retreadTyreId=${retreadTyreId}&type=${type}`);
  };

  const handleSelectAllNew = () => {
    const pageIds = pagedNewTyres.map((t) => t.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedNewTyreIds.includes(id));

    if (allSelected) {
      setSelectedNewTyreIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      setSelectAllNew(false);
    } else {
      setSelectedNewTyreIds((prev) => Array.from(new Set([...prev, ...pageIds])));
      setSelectAllNew(true);
    }
  };

  const handleSelectAllRetread = () => {
    const pageIds = pagedRetreadTyres.map((t) => t.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedRetreadTyreIds.includes(id));

    if (allSelected) {
      setSelectedRetreadTyreIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      setSelectAllRetread(false);
    } else {
      setSelectedRetreadTyreIds((prev) => Array.from(new Set([...prev, ...pageIds])));
      setSelectAllRetread(true);
    }
  };

  const toggleSelectNew = (id) => {
    setSelectedNewTyreIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectRetread = (id) => {
    setSelectedRetreadTyreIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    const pageIds = pagedNewTyres.map((t) => t.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedNewTyreIds.includes(id));
    setSelectAllNew(allSelected);
  }, [pagedNewTyres, selectedNewTyreIds]);

  useEffect(() => {
    const pageIds = pagedRetreadTyres.map((t) => t.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedRetreadTyreIds.includes(id));
    setSelectAllRetread(allSelected);
  }, [pagedRetreadTyres, selectedRetreadTyreIds]);

  const handleDeleteNew = async () => {
    try {
      if (selectedNewTyreIds.length === 0) {
        return toast.warn("Please select at least one tyre to delete!");
      }

      const { data } = await axios.post(
        `${API_BASE}/new/delete`,
        { ids: selectedNewTyreIds, loginUserId },
        requestConfig
      );

      if (!data?.success) {
        throw new Error(data?.message || "Delete failed");
      }

      toast.success(data.message || "Deleted successfully");
      fetchNewList();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete error");
    }
  };

  const handleDeleteRetread = async () => {
    try {
      if (selectedRetreadTyreIds.length === 0) {
        return toast.warn("Please select at least one retread tyre to delete!");
      }

      const { data } = await axios.post(
        `${API_BASE}/retread/delete`,
        { ids: selectedRetreadTyreIds, loginUserId },
        requestConfig
      );

      if (!data?.success) {
        throw new Error(data?.message || "Delete failed");
      }

      toast.success(data.message || "Deleted successfully");
      fetchRetreadList();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete error");
    }
  };

  const clearNewFilters = () => {
    setTyreSerialNo("");
    setVehicleName("");
    setTyreSize("");
    setVehicleRegNo("");
    setVehicleCode("");
    setTyreMake("");
    setStandViewAll("stand");

    setTimeout(() => {
      fetchNewList();
    }, 0);
  };

  const clearRetreadFilters = () => {
    setRetreadTyreSerialNo("");
    setRetreadTyreSize("");
    setTyrePosition("Select");

    setTimeout(() => {
      fetchRetreadList();
    }, 0);
  };

  const PaginationBlock = ({ page, setPage, pageSize, setPageSize, total, totalPages }) => {
    const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);

    return (
      <div className="flex flex-col md:flex-row items-center justify-between gap-3 mt-4">
        <div className="text-sm">
          Showing {from} - {to} of {total}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm">Rows:</span>
          <select
            className={selectClass}
            style={{ width: 90 }}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>

          <button
            className={`px-3 py-2 rounded border ${isDark ? "border-gray-600" : "border-gray-300"}`}
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>

          <div className="text-sm font-semibold px-2">
            {page} / {totalPages}
          </div>

          <button
            className={`px-3 py-2 rounded border ${isDark ? "border-gray-600" : "border-gray-300"}`}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TYRE DETAILS MANAGEMENT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header Section */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                New Tyre Details
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate("/vehicle/AddNewTyre")}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
              >
                <FaPlus className="w-4 h-4" />
                Add New Tyre
              </button>

              <button
                onClick={() => navigate("/vehicle/AddRetrededTyre")}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
              >
                <FaPlus className="w-4 h-4" />
                Add Retered Tyre
              </button>
            </div>
          </div>

          {/* Filters Section */}
          {showFilters && (
            <div className={`rounded-xl p-6 mb-6 border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Tyre Serial No</label>
                  <input
                    value={tyreSerialNo}
                    onChange={(e) => setTyreSerialNo(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    value={vehicleName}
                    onChange={(e) => setVehicleName(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Tyre Size</label>
                  <input
                    value={tyreSize}
                    onChange={(e) => setTyreSize(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Reg No</label>
                  <input
                    value={vehicleRegNo}
                    onChange={(e) => setVehicleRegNo(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    value={vehicleCode}
                    onChange={(e) => setVehicleCode(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Tyre Make</label>
                  <input
                    value={tyreMake}
                    onChange={(e) => setTyreMake(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="flex justify-end items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 gap-3">
                <button
                  onClick={clearNewFilters}
                  className={`px-6 py-3 rounded-lg font-semibold border ${
                    isDark ? "border-gray-600 text-white hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  View All
                </button>

                <button
                  onClick={fetchNewList}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <FaSearch />
                  {loadingNew ? "Loading..." : "Search Records"}
                </button>
              </div>
            </div>
          )}

          {/* New Tyre Details Table */}
          <div className="mb-8">
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
              New Tyre Details
            </h3>

            <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Branch Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Purchase Date</th>
                      <th className="px-4 py-4 text-center font-semibold">Tyre Size</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Code</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle RegNo</th>
                      <th className="px-4 py-4 text-center font-semibold">Tyre SerialNo</th>
                      <th className="px-4 py-4 text-center font-semibold">Tyre Make</th>
                      <th className="px-4 py-4 text-center font-semibold">Tyre Model</th>
                      <th className="px-4 py-4 text-center font-semibold">Tyre Position</th>
                      <th className="px-4 py-4 text-center font-semibold">Purchase From</th>
                      <th className="px-4 py-4 text-center font-semibold">Tyre Amount</th>
                      <th className="px-4 py-4 text-center font-semibold">Edit</th>
                      <th className="px-4 py-4 text-center font-semibold">
                        <div className="flex items-center justify-center">
                          Select All
                          <input
                            type="checkbox"
                            checked={selectAllNew}
                            onChange={handleSelectAllNew}
                            className="ml-2"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {newTyres.length === 0 ? (
                      <tr>
                        <td colSpan={14} className="px-4 py-6 text-center text-sm text-gray-500">
                          {loadingNew ? "Loading..." : "No records found"}
                        </td>
                      </tr>
                    ) : (
                      pagedNewTyres.map((tyre, idx) => (
                        <tr
                          key={tyre.id}
                          className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                            isDark ? "bg-gray-900" : "bg-white"
                          }`}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white text-center">
                            {(newPage - 1) * newPageSize + idx + 1}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.branchName}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.purchaseDate}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.tyreSize}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.vehicleCode}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.vehicleRegNo}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.tyreSerialNo}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.tyreMake}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.tyreModel}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.tyrePosition}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.purchaseFrom}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.tyreAmount}</td>

                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleEditNewTyre(tyre.id)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded text-white ${buttonClass} ${hoverClass}`}
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                          </td>

                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedNewTyreIds.includes(tyre.id)}
                              onChange={() => toggleSelectNew(tyre.id)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <PaginationBlock
              page={newPage}
              setPage={setNewPage}
              pageSize={newPageSize}
              setPageSize={setNewPageSize}
              total={newTotal}
              totalPages={newTotalPages}
            />

            <div className="flex justify-end mt-4">
              <button
                onClick={handleDeleteNew}
                className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                  selectedNewTyreIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : `${buttonClass} ${hoverClass}`
                }`}
                disabled={selectedNewTyreIds.length === 0}
              >
                <FaTrash /> Delete Selected
              </button>
            </div>
          </div>

          {/* Retread Tyre Details Section */}
          <div className="mt-12">
            <h3 className={`text-2xl font-bold mb-6 ${isDark ? "text-white" : "text-gray-900"}`}>
              Retread Tyre Details
            </h3>

            <div className={`rounded-xl p-6 mb-6 border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
              <h4 className="text-lg font-semibold mb-4">Retread Type Details</h4>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="existing"
                      checked={retreadTyreType === "existing"}
                      onChange={() => setRetreadTyreType("existing")}
                      className="mr-2"
                    />
                    Existing Tyre
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="new"
                      checked={retreadTyreType === "new"}
                      onChange={() => setRetreadTyreType("new")}
                      className="mr-2"
                    />
                    New Tyre
                  </label>
                </div>

                <div>
                  <label className={labelClass}>Tyre Serial No</label>
                  <input
                    value={retreadTyreSerialNo}
                    onChange={(e) => setRetreadTyreSerialNo(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Tyre Size</label>
                  <input
                    value={retreadTyreSize}
                    onChange={(e) => setRetreadTyreSize(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Tyre Position</label>
                  <select
                    value={tyrePosition}
                    onChange={(e) => setTyrePosition(e.target.value)}
                    className={selectClass}
                  >
                    <option value="Select">Select</option>
                    <option value="Front">Front</option>
                    <option value="Rear">Rear</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 gap-3">
                <button
                  onClick={clearRetreadFilters}
                  className={`px-6 py-3 rounded-lg font-semibold border ${
                    isDark ? "border-gray-600 text-white hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  View All
                </button>

                <button
                  onClick={fetchRetreadList}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <FaSearch />
                  {loadingRetread ? "Loading..." : "Search Records"}
                </button>
              </div>
            </div>

            <div className="mb-8">
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
                Selected Retread Tyre Details
              </h3>

              <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={`${buttonClass} text-white`}>
                      <tr>
                        <th className="px-4 py-4 text-center font-semibold">S.No</th>
                        <th className="px-4 py-4 text-center font-semibold">Branch Name</th>
                        <th className="px-4 py-4 text-center font-semibold">Retread Date</th>
                        <th className="px-4 py-4 text-center font-semibold">Tyre SerialNo</th>
                        <th className="px-4 py-4 text-center font-semibold">Tyre Size</th>
                        <th className="px-4 py-4 text-center font-semibold">Tyre Position</th>
                        <th className="px-4 py-4 text-center font-semibold">Type of Retreaded</th>
                        <th className="px-4 py-4 text-center font-semibold">No of Retreaded</th>
                        <th className="px-4 py-4 text-center font-semibold">Retreaded from</th>
                        <th className="px-4 py-4 text-center font-semibold">Amount</th>
                        <th className="px-4 py-4 text-center font-semibold">Edit</th>
                        <th className="px-4 py-4 text-center font-semibold">
                          <div className="flex items-center justify-center">
                            Select All
                            <input
                              type="checkbox"
                              checked={selectAllRetread}
                              onChange={handleSelectAllRetread}
                              className="ml-2"
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {retreadTyres.length === 0 ? (
                        <tr>
                          <td colSpan={12} className="px-4 py-6 text-center text-sm text-gray-500">
                            {loadingRetread ? "Loading..." : "No records found"}
                          </td>
                        </tr>
                      ) : (
                        pagedRetreadTyres.map((tyre, idx) => (
                          <tr
                            key={tyre.id}
                            className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                              isDark ? "bg-gray-900" : "bg-white"
                            }`}
                          >
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white text-center">
                              {(retPage - 1) * retPageSize + idx + 1}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.branchName}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.retreadDate}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.tyreSerialNo}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.tyreSize}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.tyrePosition}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.typeOfRetreaded}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.noOfRetreaded}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.retreadedFrom}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">{tyre.amount}</td>

                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => handleEditRetreadTyre(tyre.id)}
                                className={`inline-flex items-center gap-2 px-3 py-2 rounded text-white ${buttonClass} ${hoverClass}`}
                                title="Edit"
                              >
                                <FaEdit />
                              </button>
                            </td>

                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={selectedRetreadTyreIds.includes(tyre.id)}
                                onChange={() => toggleSelectRetread(tyre.id)}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <PaginationBlock
                page={retPage}
                setPage={setRetPage}
                pageSize={retPageSize}
                setPageSize={setRetPageSize}
                total={retTotal}
                totalPages={retTotalPages}
              />

              <div className="flex justify-end mt-4">
                <button
                  onClick={handleDeleteRetread}
                  className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                    selectedRetreadTyreIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : `${buttonClass} ${hoverClass}`
                  }`}
                  disabled={selectedRetreadTyreIds.length === 0}
                >
                  <FaTrash /> Delete Selected
                </button>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default TyreDetails;